import React from 'react'
import { Stack, Box, useTheme, useMediaQuery } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { minBy } from 'lodash'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { DateRangeFilterButton, DateRangeValue } from '@e3dc-react/shell/Elements/DatePicker'
import { ChargingSessionDesktopTable } from './ChargingSessionsDesktopTableView'
import { useChargingSessions } from '../hooks/use-charging-sessions'
import { ClearFiltersButton } from '../../shared/components/ClearFiltersButton/ClearFiltersButton'
import { EMobilityChargingSessionsSkeleton } from './EMobilityChargingSessionsSkeleton'
import { useAuthenticationFilterOptions } from '../../shared/hooks/filter-options/use-authentication-filter-options'
import { useHagerBridgePermissions } from '../../shared'
import { ChargingSessionMobileTableView } from './mobile/ChargingSessionsMobileTableView'
import { ChargingSessionsExportButton } from './ChargingSessionsExportButton'
import { useWallboxFilterOptions } from '../../shared/hooks/filter-options/use-wallbox-filter-options'
import { useChargingSessionsStatusFilterOptions } from '../../shared/hooks/filter-options/use-charging-sessios-status-filter-options'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { endOfDay, format, isValid, startOfDay } from '@e3dc-react/shell/Libs/date'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { updateChargingSessionsFilters } from 'src/State/E-Mobility/EMobilityStateAction'
import { ChargingSessionsMobileFilters } from './mobile/ChargingSessionsMobileFilters'
import { ChargingSessionsFilters } from './ChargingSessionsFilters'
import { ChargingSessionMobileSort } from './mobile/ChargingSessionMobileSort'
import { ChargingSessionSortMenu } from './ChargingSessionSortMenu'

/**
 * Charging session tab is rich in data and UI elements(multiple filters, sort selectors, download button) and needs precise
 * tweaking of the layout based on the viewport size. The built-in breakpoints from the theme do not really cover the
 * viewport ranges necessary to make the UI consistent across screen sizes.
 */
const CUSTOM_MOBILE_BREAKPOINT = 820

const useStyles = makeStyles()(theme => ({
  tableContainer: {
    marginTop: theme.spacing(2),
  },
}))

interface EMobilityChargingSessionsProps {
  installationId: number
}

/**
 * Top level component for the charging session feature.
 *
 * Renders the charging session table, filters and export button. Holds table and filter state.
 */
export const EMobilityChargingSessions: React.FC<EMobilityChargingSessionsProps> = ({ installationId }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { classes } = useStyles()
  /** charging session table is super rich in number of columns and amount of content, so it looks best at breakpoints > "l"("xl" and above)  */
  const isLaptopOrSmaller = useMediaQuery(theme.breakpoints.down('lg'))
  const isMobile = useMediaQuery(theme.breakpoints.down(CUSTOM_MOBILE_BREAKPOINT))
  const isLaptopOrTablet = useMediaQuery(theme.breakpoints.between(CUSTOM_MOBILE_BREAKPOINT, 'lg'))

  const chargingSessionFilters = useTypedSelector(state => state.eMobility.chargingSessionsFilters.filters)
  const dispatch = useDispatch()

  const startDate = new Date(chargingSessionFilters?.startDate ?? '')
  const endDate = new Date(chargingSessionFilters?.endDate ?? '')

  const dateRange: DateRangeValue | undefined =
    isValid(startDate) && isValid(new Date(endDate))
      ? {
          startDate: startOfDay(startDate),
          endDate: endOfDay(new Date(endDate)),
        }
      : undefined

  const { data, unfilteredData, isLoading, isError } = useChargingSessions({
    installationId,
    wallboxId: chargingSessionFilters?.wallboxIds,
    startDate: dateRange?.startDate?.toISOString(),
    endDate: dateRange?.endDate?.toISOString(),
    midMeter: chargingSessionFilters?.midMeter ?? [],
    ocpiTokenIds: chargingSessionFilters?.ocpiTokenIds,
    status: chargingSessionFilters?.statuses,
  })

  const hasColumnFilters =
    chargingSessionFilters?.wallboxIds?.length ||
    chargingSessionFilters?.statuses?.length ||
    chargingSessionFilters?.ocpiTokenIds?.length ||
    chargingSessionFilters?.midMeter?.length ||
    Boolean(dateRange)
  const maxDate = endOfDay(new Date())

  const { wallboxOptions, wallboxes, isLoadingWallboxes } = useWallboxFilterOptions(unfilteredData, installationId)
  const { authenticationOptions, isChargingCardsLoading } = useAuthenticationFilterOptions(unfilteredData, installationId)
  const statusFilterOptions = useChargingSessionsStatusFilterOptions(unfilteredData)

  const hasChargingSessionMidEnergy = unfilteredData?.some(session => session.midInfo.midMeterConnected)
  const hasWallboxMidMeter = wallboxes.some(wallbox => wallbox.useMidMeter)
  const shouldDisplayMidMeter = hasChargingSessionMidEnergy || hasWallboxMidMeter

  const {
    permissions: { sessionPermissions, tourGuidePermissions },
  } = useHagerBridgePermissions()

  const firstSessionCreated = minBy(unfilteredData, session => session.startDateTime)
  const formattedFirstSession = firstSessionCreated ? new Date(firstSessionCreated.startDateTime) : new Date()

  if (isError) {
    return <ErrorMessage title={t('emobility.chargingSession.errorMessage')} />
  }

  if (isLoading || isLoadingWallboxes || isChargingCardsLoading || !data) {
    return <EMobilityChargingSessionsSkeleton />
  }

  return (
    <Box>
      {tourGuidePermissions.viewTourGuide && <BannerQueue location={BannerLocations.EmobilityTour} />}
      <BannerQueue location={BannerLocations.EmobilityCountryInfoMissingSessions} />

      <Stack direction="row" flexWrap={'wrap'} justifyContent={'space-between'}>
        <Stack direction="row" flexWrap={'wrap'} gap={2} width="100%">
          <DateRangeFilterButton
            title={t('emobility.chargingSession.timeRange')}
            value={dateRange}
            minDate={formattedFirstSession}
            maxDate={maxDate}
            onChange={dateRange => {
              dispatch(
                updateChargingSessionsFilters({
                  startDate: dateRange?.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : '',
                  endDate: dateRange?.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : '',
                  page: 0,
                })
              )
            }}
          />
          {isMobile ? (
            <ChargingSessionsMobileFilters
              wallboxOptions={wallboxOptions}
              authenticationOptions={authenticationOptions}
              shouldDisplayMidMeter={shouldDisplayMidMeter}
              sessionStatusFilterOptions={statusFilterOptions}
            />
          ) : (
            <ChargingSessionsFilters
              wallboxFilterOptions={wallboxOptions}
              ocpiTokenIdsFilterOptions={authenticationOptions}
              sessionStatusFilterOptions={statusFilterOptions}
              shouldDisplayMidMeter={shouldDisplayMidMeter}
            />
          )}
          {isMobile ? <ChargingSessionMobileSort /> : null}
          {hasColumnFilters ? (
            <ClearFiltersButton
              onClick={() => {
                dispatch(
                  updateChargingSessionsFilters({
                    wallboxIds: [],
                    ocpiTokenIds: [],
                    midMeter: [],
                    statuses: [],
                    startDate: undefined,
                    endDate: undefined,
                    page: 0,
                  })
                )
              }}
            />
          ) : null}
          <Box flex={1} />
          {isLaptopOrTablet ? (
            <ChargingSessionSortMenu
              value={{ name: chargingSessionFilters?.sort?.name ?? 'startDateTime', direction: chargingSessionFilters?.sort?.direction ?? 'desc' }}
              shouldDisplayMidMeter={shouldDisplayMidMeter}
              onChange={sort =>
                dispatch(
                  updateChargingSessionsFilters({
                    ...chargingSessionFilters,
                    sort,
                    page: 0,
                  })
                )
              }
            />
          ) : null}

          {sessionPermissions.exportSessions && (
            <ChargingSessionsExportButton
              installationId={installationId}
              dateRange={dateRange}
              wallboxId={chargingSessionFilters?.wallboxIds ?? []}
              ocpiTokenIds={chargingSessionFilters?.ocpiTokenIds ?? []}
              midMeter={chargingSessionFilters?.midMeter ?? []}
            />
          )}
        </Stack>
      </Stack>

      <Box className={classes.tableContainer}>
        <Box className={classes.tableContainer}>
          {isLaptopOrSmaller ? (
            <ChargingSessionMobileTableView shouldDisplayMidMeter={shouldDisplayMidMeter} chargingSession={data} />
          ) : (
            <ChargingSessionDesktopTable shouldDisplayMidMeter={shouldDisplayMidMeter} chargingSessions={data} installationId={installationId} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
